import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { useSEO, SITE_URL } from '../../../hooks/useSEO';

const BusinessContinuityPage = () => {
  useSEO({
    title: 'Business Continuity & Incident Response for Financial Firms | Mortgage Moat',
    description: 'Build a resilient mortgage business with disaster recovery planning, incident response procedures, and business continuity strategies. Stay operational during cyber incidents.',
    keywords: 'business continuity mortgage, incident response plan, disaster recovery financial, cyber incident response, GLBA business continuity',
    canonical: '/knowledge-center/business-continuity',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Business Continuity & Incident Response for Financial Firms',
        description: 'Ensuring operational resilience through disaster recovery planning, incident response procedures, and business continuity strategies for mortgage operations.',
        url: `${SITE_URL}/knowledge-center/business-continuity`,
        publisher: { '@type': 'Organization', name: 'Mortgage Moat', url: SITE_URL },
        author: { '@type': 'Organization', name: 'Mortgage Moat' },
        dateModified: '2025-07-14',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Knowledge Center', item: `${SITE_URL}/knowledge-center/business-continuity` },
          { '@type': 'ListItem', position: 3, name: 'Business Continuity', item: `${SITE_URL}/knowledge-center/business-continuity` },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-indigo-50 via-white to-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-restart-line"></i>
                Security Knowledge Center
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Business Continuity & <span className="text-indigo-600">Incident Response</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Ensuring operational resilience through disaster recovery planning, incident response procedures, and business continuity strategies that keep your mortgage operations running during crises
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button variant="primary" size="lg" className="whitespace-nowrap">
                  <i className="ri-file-shield-2-line mr-2"></i>
                  Build Your Plan
                </Button>
                <Button variant="outline" size="lg" className="whitespace-nowrap">
                  <i className="ri-calendar-check-line mr-2"></i>
                  Schedule Consultation
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="text-5xl font-bold text-indigo-600 mb-2">93%</div>
                <p className="text-gray-700 font-semibold">Companies Without BC Plan Fail</p>
                <p className="text-sm text-gray-600 mt-2">Within one year of major disaster</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-indigo-600 mb-2">$8,600</div>
                <p className="text-gray-700 font-semibold">Cost Per Minute of Downtime</p>
                <p className="text-sm text-gray-600 mt-2">For financial services firms</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-indigo-600 mb-2">54%</div>
                <p className="text-gray-700 font-semibold">Have Experienced Major Incident</p>
                <p className="text-sm text-gray-600 mt-2">In the past 24 months</p>
              </div>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-indigo-50 border-l-4 border-indigo-600 p-6">
                <div className="flex items-start gap-4">
                  <i className="ri-alarm-warning-line text-3xl text-indigo-600 flex-shrink-0"></i>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Regulatory Requirement & Business Necessity</h3>
                    <p className="text-gray-700">
                      Federal regulators require financial institutions to have <strong>documented business continuity and disaster recovery plans</strong>. Beyond compliance, these plans are essential for protecting your business, maintaining customer trust, and ensuring you can continue operations during ransomware attacks, natural disasters, or system failures.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Components */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Essential Business Continuity Components</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Building a comprehensive resilience framework for your organization
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-indigo-600">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-file-list-3-line text-3xl text-indigo-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Business Impact Analysis (BIA)</h3>
                <p className="text-gray-700 mb-4">
                  Identify critical business functions, assess potential impacts of disruptions, and determine recovery priorities.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Critical processes:</strong> Loan origination, funding, servicing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Recovery time objectives (RTO):</strong> Maximum acceptable downtime</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Recovery point objectives (RPO):</strong> Acceptable data loss</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Financial impact:</strong> Cost of downtime per hour/day</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-purple-600">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-restart-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Disaster Recovery Plan (DRP)</h3>
                <p className="text-gray-700 mb-4">
                  Technical procedures for restoring IT systems, data, and infrastructure after a disruption.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Backup strategies:</strong> Automated, tested, offsite backups</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>System recovery:</strong> Step-by-step restoration procedures</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Alternative infrastructure:</strong> Cloud failover or hot sites</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Data restoration:</strong> Prioritized recovery sequences</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-red-600">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-alarm-warning-line text-3xl text-red-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Incident Response Plan (IRP)</h3>
                <p className="text-gray-700 mb-4">
                  Structured approach to detecting, containing, and recovering from cybersecurity incidents.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Detection & analysis:</strong> Identifying security incidents</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Containment:</strong> Isolating affected systems quickly</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Eradication:</strong> Removing threats from environment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Recovery & lessons learned:</strong> Restoration and improvement</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-orange-600">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-team-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Crisis Management Team</h3>
                <p className="text-gray-700 mb-4">
                  Designated personnel with clear roles and responsibilities for managing business disruptions.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Incident commander:</strong> Overall response coordination</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Technical lead:</strong> IT systems and data recovery</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Communications lead:</strong> Stakeholder notifications</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Legal/compliance:</strong> Regulatory reporting requirements</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Incident Response Lifecycle */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Incident Response Lifecycle</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Six-phase approach to handling cybersecurity incidents effectively
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-white rounded-xl p-8 border-l-4 border-indigo-600">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-xl">1</div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Preparation</h3>
                      <p className="text-gray-700 mb-4">
                        Establish incident response capabilities, train team members, and implement monitoring tools before incidents occur.
                      </p>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-sm font-semibold text-gray-900 mb-1">Documentation</p>
                          <p className="text-xs text-gray-600">Response procedures & playbooks</p>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-sm font-semibold text-gray-900 mb-1">Training</p>
                          <p className="text-xs text-gray-600">Regular team exercises & drills</p>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-sm font-semibold text-gray-900 mb-1">Tools</p>
                          <p className="text-xs text-gray-600">SIEM, EDR, forensic capabilities</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-white rounded-xl p-8 border-l-4 border-indigo-600">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-xl">2</div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Detection & Analysis</h3>
                      <p className="text-gray-700 mb-4">
                        Identify potential security incidents through monitoring, alerts, and user reports, then analyze to determine scope and severity.
                      </p>
                      <ul className="grid md:grid-cols-2 gap-3 text-sm">
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                          <span className="text-gray-700">Monitor security alerts and logs</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                          <span className="text-gray-700">Validate and prioritize incidents</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                          <span className="text-gray-700">Document initial findings</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                          <span className="text-gray-700">Assess impact and scope</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-white rounded-xl p-8 border-l-4 border-indigo-600">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-xl">3</div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Containment</h3>
                      <p className="text-gray-700 mb-4">
                        Limit the spread and impact of the incident through short-term and long-term containment strategies.
                      </p>
                      <div className="bg-white rounded-lg p-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <p className="font-semibold text-gray-900 mb-2 text-sm">Short-term Actions</p>
                            <ul className="space-y-1 text-xs text-gray-600">
                              <li className="flex items-start gap-2">
                                <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                                <span>Isolate affected systems</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                                <span>Block malicious IPs/domains</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                                <span>Disable compromised accounts</span>
                              </li>
                            </ul>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 mb-2 text-sm">Long-term Actions</p>
                            <ul className="space-y-1 text-xs text-gray-600">
                              <li className="flex items-start gap-2">
                                <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                                <span>Patch vulnerabilities</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                                <span>Update security controls</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                                <span>Strengthen monitoring</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-white rounded-xl p-8 border-l-4 border-indigo-600">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-xl">4</div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Eradication</h3>
                      <p className="text-gray-700 mb-4">
                        Remove the threat from your environment completely, including malware, unauthorized access, and vulnerabilities.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-3 py-2 rounded-lg">Remove malware</span>
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-3 py-2 rounded-lg">Close backdoors</span>
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-3 py-2 rounded-lg">Reset credentials</span>
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-3 py-2 rounded-lg">Patch systems</span>
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-3 py-2 rounded-lg">Verify clean state</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-white rounded-xl p-8 border-l-4 border-indigo-600">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-xl">5</div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Recovery</h3>
                      <p className="text-gray-700 mb-4">
                        Restore systems to normal operations while monitoring for signs of attacker persistence or reinfection.
                      </p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-4">
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm flex items-center gap-2">
                            <i className="ri-restart-line text-indigo-600"></i>
                            System Restoration
                          </h4>
                          <p className="text-xs text-gray-600">Rebuild or restore from clean backups, verify integrity, gradually return to production</p>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm flex items-center gap-2">
                            <i className="ri-eye-line text-indigo-600"></i>
                            Enhanced Monitoring
                          </h4>
                          <p className="text-xs text-gray-600">Increased surveillance for indicators of compromise or attacker return attempts</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-white rounded-xl p-8 border-l-4 border-indigo-600">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-xl">6</div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Post-Incident Activity</h3>
                      <p className="text-gray-700 mb-4">
                        Conduct thorough review to document lessons learned and improve future incident response capabilities.
                      </p>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-file-text-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                          <span><strong>Incident report:</strong> Complete documentation of timeline, actions, and outcomes</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-team-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                          <span><strong>Team debrief:</strong> What worked well, what needs improvement</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-tools-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                          <span><strong>Process updates:</strong> Revise procedures based on lessons learned</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-shield-check-line text-indigo-600 flex-shrink-0 mt-0.5"></i>
                          <span><strong>Security improvements:</strong> Implement controls to prevent recurrence</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Communication Plan */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Crisis Communication Strategy</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Managing stakeholder communications during incidents and disruptions
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-user-line text-2xl text-indigo-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Customers</h4>
                <p className="text-sm text-gray-700 mb-3">Transparent updates on service status and data protection</p>
                <p className="text-xs text-gray-600">Timing: Within 24-72 hours of confirmed breach</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-team-line text-2xl text-indigo-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Employees</h4>
                <p className="text-sm text-gray-700 mb-3">Clear instructions on roles, responsibilities, and procedures</p>
                <p className="text-xs text-gray-600">Timing: Immediate notification to response team</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-government-line text-2xl text-indigo-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Regulators</h4>
                <p className="text-sm text-gray-700 mb-3">Formal breach notifications per state and federal requirements</p>
                <p className="text-xs text-gray-600">Timing: As required by law (often 72 hours)</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-newspaper-line text-2xl text-indigo-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Media</h4>
                <p className="text-sm text-gray-700 mb-3">Prepared statements through designated spokesperson only</p>
                <p className="text-xs text-gray-600">Timing: After customer and regulator notification</p>
              </div>
            </div>

            <div className="max-w-4xl mx-auto mt-8">
              <div className="bg-red-50 border-l-4 border-red-600 p-6">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-alert-line text-red-600"></i>
                  Breach Notification Requirements
                </h4>
                <p className="text-sm text-gray-700 mb-3">
                  Financial services firms must comply with multiple notification requirements:
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-red-600 flex-shrink-0 mt-0.5"></i>
                    <span><strong>State laws:</strong> Notify affected individuals within 30-90 days (varies by state)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-red-600 flex-shrink-0 mt-0.5"></i>
                    <span><strong>GLBA:</strong> Report to primary federal regulator and law enforcement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-red-600 flex-shrink-0 mt-0.5"></i>
                    <span><strong>NYDFS:</strong> Notify within 72 hours of determination (for NY-regulated entities)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-red-600 flex-shrink-0 mt-0.5"></i>
                    <span><strong>Credit bureaus:</strong> Notify if breach affects 1,000+ individuals</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Testing & Maintenance */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Testing & Maintaining Your Plans</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Regular testing ensures your plans work when you need them most
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <div className="bg-gradient-to-br from-indigo-50 to-white rounded-xl p-8 text-center">
                <div className="w-20 h-20 bg-indigo-600 text-white rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-file-text-line text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Tabletop Exercises</h3>
                <p className="text-gray-700 mb-4 text-sm">
                  Discussion-based scenarios where team members walk through response procedures
                </p>
                <p className="text-xs text-indigo-600 font-semibold">Frequency: Quarterly</p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-white rounded-xl p-8 text-center">
                <div className="w-20 h-20 bg-indigo-600 text-white rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-play-circle-line text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Simulation Drills</h3>
                <p className="text-gray-700 mb-4 text-sm">
                  Hands-on exercises testing actual recovery procedures and system restoration
                </p>
                <p className="text-xs text-indigo-600 font-semibold">Frequency: Semi-annually</p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-white rounded-xl p-8 text-center">
                <div className="w-20 h-20 bg-indigo-600 text-white rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-refresh-line text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Plan Updates</h3>
                <p className="text-gray-700 mb-4 text-sm">
                  Review and revise plans based on organizational changes and lessons learned
                </p>
                <p className="text-xs text-indigo-600 font-semibold">Frequency: Annually</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Build Your Business Continuity Framework
            </h2>
            <p className="text-xl text-indigo-100 mb-8 max-w-3xl mx-auto">
              Our comprehensive business continuity and incident response solutions help you prepare for, respond to, and recover from disruptions while maintaining regulatory compliance and customer trust.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="outline" size="lg" className="bg-white text-indigo-600 hover:bg-gray-100 border-white whitespace-nowrap">
                <i className="ri-file-list-3-line mr-2"></i>
                Download BC/DR Template
              </Button>
              <Button variant="primary" size="lg" className="bg-white text-indigo-600 hover:bg-gray-100 whitespace-nowrap">
                <i className="ri-customer-service-2-line mr-2"></i>
                Schedule Planning Session
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BusinessContinuityPage;